import React, { useMemo } from "react";
import { STADIUM_VIEWBOX, ZONE_PATHS, pathCentroid } from "@/data/stadiumPaths";
import { DoorOpen, Camera as CameraIcon } from "lucide-react";

/**
 * Layered top-down stadium intelligence map.
 *
 * Props:
 *   zones          : bootstrap zones (id, name, type, capacity, current_occupancy)
 *   heatmap        : crowd heatmap entries  { zone_id, pct, occupancy, capacity, name }
 *   parking        : parking snapshot lots  [{ zone_id, name, pct, vehicles_in_lot }]
 *   cameras        : camera list            [{ id, name, zone_id, status, ptz }]
 *   events         : recent events / alerts (filtered live list)
 *   layers         : { crowd, alert, cameras, security, exits, parking }
 *   onZoneClick    : (zoneId) => void
 */

const crowdHeat = (pct) => {
  if (pct >= 0.9)  return "hsl(var(--destructive))";
  if (pct >= 0.75) return "hsl(var(--warning))";
  if (pct >= 0.55) return "hsl(var(--primary))";
  if (pct >= 0.3)  return "hsl(217 91% 60%)";
  return "hsl(var(--muted-foreground))";
};

const alertHeat = (score) => {
  // score: 0..1 (composite of severity counts)
  if (score >= 0.7) return "hsl(var(--destructive))";
  if (score >= 0.4) return "hsl(var(--warning))";
  if (score >= 0.2) return "hsl(var(--primary))";
  return "hsl(var(--muted-foreground))";
};

const SEV_WEIGHT = { critical: 1.0, high: 0.65, medium: 0.35, low: 0.15 };
const SECURITY_TYPES = new Set([
  "intrusion", "fight", "fence_climb", "abandoned_bag",
  "unauthorized_vehicle", "loitering", "vip_proximity",
]);

// Emergency exit anchor points (manually placed near the 4 main gates + 2 side exits)
const EXITS = [
  { id: "ex-a", x: 190, y: 92,  zone_id: "gate-a" },
  { id: "ex-b", x: 810, y: 92,  zone_id: "gate-b" },
  { id: "ex-c", x: 190, y: 612, zone_id: "gate-c" },
  { id: "ex-d", x: 810, y: 612, zone_id: "gate-d" },
  { id: "ex-w", x: 110, y: 360, zone_id: "stand-west" },
  { id: "ex-e", x: 920, y: 360, zone_id: "concourse-east" },
];

export const StadiumIntelMap = ({
  zones = [],
  heatmap = [],
  parking = [],
  cameras = [],
  events = [],
  layers = {
    crowd: true,
    alert: false,
    cameras: false,
    security: false,
    exits: true,
    parking: false,
  },
  onZoneClick,
}) => {
  const heatById   = useMemo(() => Object.fromEntries(heatmap.map(h => [h.zone_id, h])), [heatmap]);
  const parkingMap = useMemo(() => Object.fromEntries(parking.map(p => [p.zone_id, p])), [parking]);
  const zonesById  = useMemo(() => Object.fromEntries(zones.map(z => [z.id, z])), [zones]);

  // Alert score per zone (0..1) based on recent events
  const alertScore = useMemo(() => {
    const scores = {};
    for (const e of events) {
      const w = SEV_WEIGHT[e.severity] ?? 0.1;
      scores[e.zone_id] = (scores[e.zone_id] || 0) + w;
    }
    // normalize roughly
    const max = Math.max(1, ...Object.values(scores));
    return Object.fromEntries(Object.entries(scores).map(([k, v]) => [k, Math.min(1, v / max)]));
  }, [events]);

  // Top 5 most recent security/critical events for incident pulses
  const securityPulses = useMemo(() => {
    return (events || [])
      .filter(e => SECURITY_TYPES.has(e.type) || e.severity === "critical")
      .slice(0, 8)
      .map((e) => {
        const d = ZONE_PATHS[e.zone_id];
        if (!d) return null;
        const c = pathCentroid(d);
        return { id: e.id, ...c, severity: e.severity, type: e.type, zone_id: e.zone_id };
      })
      .filter(Boolean);
  }, [events]);

  // Build camera pin positions: spread cameras evenly inside their zone centroid
  const cameraPins = useMemo(() => {
    const byZone = {};
    for (const c of cameras) {
      (byZone[c.zone_id] ||= []).push(c);
    }
    const pins = [];
    for (const [zid, list] of Object.entries(byZone)) {
      const d = ZONE_PATHS[zid];
      if (!d) continue;
      const center = pathCentroid(d);
      list.forEach((cam, idx) => {
        const angle = (idx / list.length) * Math.PI * 2;
        const r = 14;
        pins.push({
          ...cam,
          x: center.x + Math.cos(angle) * r,
          y: center.y + Math.sin(angle) * r,
        });
      });
    }
    return pins;
  }, [cameras]);

  return (
    <div className="w-full h-full relative" data-testid="stadium-intel-map">
      <svg
        viewBox={STADIUM_VIEWBOX}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="simPitchGlow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.16" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pulseAlert" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity="0.95" />
            <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Stadium blueprint backdrop (Wankhede Stadium) */}
        <image
          href="/stadium-backdrop.png"
          x="0" y="0" width="1000" height="720"
          preserveAspectRatio="xMidYMid meet"
          opacity="0.18"
          style={{ pointerEvents: "none" }}
        />

        {/* Pitch */}
        <ellipse cx="500" cy="360" rx="220" ry="160" fill="hsl(var(--surface-elevated))" stroke="hsl(var(--border))" strokeWidth="1.5" />
        <ellipse cx="500" cy="360" rx="220" ry="160" fill="url(#simPitchGlow)" />
        <rect x="486" y="320" width="28" height="80" fill="hsl(var(--muted))" stroke="hsl(var(--border))" />
        <text x="500" y="365" textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))" fontFamily="IBM Plex Mono, monospace" letterSpacing="3">Pitch</text>

        {/* Zone polygons */}
        {Object.entries(ZONE_PATHS).map(([zid, d]) => {
          const z = zonesById[zid];
          const isParking = z?.type === "parking";
          const isGate    = z?.type === "gate";
          const heat = heatById[zid];
          const ascore = alertScore[zid] || 0;
          const parkInfo = parkingMap[zid];

          let fill = "hsl(var(--surface-elevated))";
          let opacity = 0.55;
          let stroke = "hsl(var(--border))";

          // Parking layer takes priority for parking zones
          if (isParking && layers.parking && parkInfo) {
            fill = crowdHeat(parkInfo.pct);
            opacity = 0.35 + parkInfo.pct * 0.55;
            stroke = fill;
          } else if (!isParking && layers.alert && ascore > 0) {
            fill = alertHeat(ascore);
            opacity = 0.25 + ascore * 0.55;
            stroke = fill;
          } else if (!isParking && layers.crowd && heat) {
            fill = crowdHeat(heat.pct);
            opacity = 0.18 + heat.pct * 0.6;
            stroke = fill;
          } else {
            fill = "hsl(var(--muted))";
            opacity = 0.22;
            stroke = "hsl(var(--border))";
          }

          const c = pathCentroid(d);
          const label = z?.name || zid;
          const sublabel = (() => {
            if (isParking && layers.parking && parkInfo) {
              return `${Math.round(parkInfo.pct * 100)}%`;
            }
            if (!isParking && layers.alert && ascore > 0) {
              return `${Math.round(ascore * 100)}`;
            }
            if (!isParking && layers.crowd && heat) {
              return `${Math.round(heat.pct * 100)}%`;
            }
            return "";
          })();

          return (
            <g
              key={zid}
              className="cursor-pointer"
              onClick={() => onZoneClick?.(zid)}
              data-testid={`stadium-map-zone-${zid}`}
            >
              <path
                d={d}
                fill={fill}
                fillOpacity={opacity}
                stroke={stroke}
                strokeOpacity={0.8}
                strokeWidth={1}
              />
              {/* Pulse outline for crowded / hot zones */}
              {layers.crowd && heat && heat.pct >= 0.9 && (
                <path d={d} fill="none" stroke="hsl(var(--destructive))" strokeWidth="1.5" className="flow-path" />
              )}
              <text
                x={c.x}
                y={c.y - 4}
                textAnchor="middle"
                fontSize={isGate ? "8" : "9"}
                fill="hsl(var(--foreground))"
                fontFamily="IBM Plex Mono, monospace"
                letterSpacing="1"
              >
                {label}
              </text>
              {sublabel && (
                <text
                  x={c.x}
                  y={c.y + 9}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="700"
                  fill="hsl(var(--foreground))"
                  fontFamily="IBM Plex Mono, monospace"
                >
                  {sublabel}
                </text>
              )}
            </g>
          );
        })}

        {/* Camera coverage cones / pins */}
        {layers.cameras && cameraPins.map((p) => (
          <g key={p.id} data-testid={`stadium-cam-${p.id}`}>
            <circle
              cx={p.x}
              cy={p.y}
              r="18"
              fill="hsl(var(--primary))"
              fillOpacity="0.08"
              stroke="hsl(var(--primary))"
              strokeOpacity="0.35"
              strokeWidth="0.75"
            />
            <circle
              cx={p.x}
              cy={p.y}
              r="3"
              fill={p.status === "online" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
              stroke="hsl(var(--background))"
              strokeWidth="0.75"
            />
          </g>
        ))}

        {/* Security incident pulses */}
        {layers.security && securityPulses.map((p, i) => (
          <g key={`pulse-${p.id}-${i}`} data-testid={`stadium-pulse-${p.id}`}>
            <circle cx={p.x} cy={p.y} r="14" fill="url(#pulseAlert)" className="map-pulse-ring" />
            <circle cx={p.x} cy={p.y} r="4" fill="hsl(var(--destructive))" stroke="hsl(var(--background))" strokeWidth="1" />
          </g>
        ))}

        {/* Emergency exits */}
        {layers.exits && EXITS.map((ex) => (
          <g key={ex.id} data-testid={`stadium-exit-${ex.id}`}>
            <rect x={ex.x - 8} y={ex.y - 8} width="16" height="16" fill="hsl(var(--success))" fillOpacity="0.85" stroke="hsl(var(--background))" strokeWidth="1" />
            <text x={ex.x} y={ex.y + 3.5} textAnchor="middle" fontSize="9" fontWeight="900" fill="hsl(var(--background))" fontFamily="Chivo, sans-serif">
              E
            </text>
          </g>
        ))}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-2 left-2 panel-elevated px-2 py-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[9px] font-mono tracking-wide max-w-[60%]">
        {(layers.crowd || layers.parking) && (
          <>
            <span className="text-muted-foreground">Density</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(217 91% 60%)" }} />30+</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--primary))" }} />55+</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--warning))" }} />75+</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--destructive))" }} />90+</span>
          </>
        )}
        {layers.exits && (
          <span className="inline-flex items-center gap-1 ml-2">
            <DoorOpen className="w-3 h-3 sev-low" /> Exit
          </span>
        )}
        {layers.cameras && (
          <span className="inline-flex items-center gap-1 ml-2">
            <CameraIcon className="w-3 h-3 text-[hsl(var(--primary))]" /> Camera
          </span>
        )}
      </div>
    </div>
  );
};

export default StadiumIntelMap;
